import logoComms          from 'assets/images/logo-comms.svg';
import logoEnergy         from 'assets/images/logo-energy.svg';
import { ombudsmanUrl }   from 'lib/ombudsmanData';
import sectorToCompanyName from 'lib/helpers/sectorToCompanyName';

export type ReferralBrand = 'energy' | 'communications';

export interface ReferralStatutoryDisclosures {
  /** Registered entity name, company number and address, plus the accessibility statement. */
  entityName: string;
  companyNumber: string;
  address: string;
  accessibilityStatementUrl: string;
}

export interface ReferralBrandConfig {
  /** Leading path segment of this brand's referral URL. */
  segment: ReferralBrand;
  /** "Energy Ombudsman" / "Communications Ombudsman". */
  displayName: string;
  logo: string;
  logoAlt: string;
  /** Sector noun used in prose, e.g. "disputes ... in the energy sector". */
  sectorNoun: string;
  regulatorName: string;
  regulatorDescription: string;
  privacyPolicyUrl: string;
  contactUrl: string;
  aboutUrl: string;
  /**
   * Support line quoted when the journey cannot continue. Optional for the same reason as
   * statutoryDisclosures: the communications number is not sourced anywhere in this repo, and
   * callers fall back to contactUrl rather than quoting the wrong Ombudsman's number.
   */
  phone?: string;
  /**
   * The "take practical action" example list in the Role of the Ombudsman modal. Brand-varying
   * because the energy wording ends in "change your tariff", which means nothing to a
   * communications customer. The communications variant omits that clause rather than inventing a
   * comms equivalent: this is a public statement about what the Ombudsman can compel a provider to
   * do, so a wrong example is worse than a shorter list. The comms wording source the ticket
   * refers to (documentation/domain-knowledge/business/communications-ombudsman/) does not exist
   * in this repo.
   */
  practicalActionText: string;
  statutoryDisclosures?: ReferralStatutoryDisclosures;
}

// src/assets/images/declaration.d.ts types an imported SVG as a component, but under CRA the
// default export is the asset URL. Coerced here so the rest of the app sees a plain string —
// the old Header did the same thing with a template literal.
const assetUrl = (svg: unknown): string => String(svg);

// Comms only has /contact-us confirmed in-repo (lib/ombudsmanData). The /privacy-policy and
// /about-us paths mirror the energy site and need confirming against commsombudsman.org before
// release — they are on a compliance-relevant page, so a 404 here is not cosmetic.
const brandUrls = (brand: ReferralBrand) => ({
  privacyPolicyUrl: `${ombudsmanUrl(brand)}/privacy-policy`,
  contactUrl:       `${ombudsmanUrl(brand)}/contact-us`,
  aboutUrl:         `${ombudsmanUrl(brand)}/about-us`,
});

export const REFERRAL_BRANDS: Record<ReferralBrand, ReferralBrandConfig> = {
  energy: {
    segment:              'energy',
    displayName:          sectorToCompanyName('energy'),
    logo:                 assetUrl(logoEnergy),
    logoAlt:              'Energy Ombudsman Logo',
    sectorNoun:           'energy',
    regulatorName:        'Ofgem',
    regulatorDescription: 'the energy regulator for Great Britain',
    // Pre-CD-8653 literal from AccountHolderPage. Note lib/ombudsmanData exports a different
    // energy number (energyPhonePretty); which is correct for the referral journey needs
    // confirming, so this preserves today's behaviour rather than silently switching it.
    phone:                '0330 041 2207',
    // Byte-identical to the pre-CD-8653 literal in OmbudsmanRoleModalContent (AC8).
    practicalActionText:  'Take practical action, e.g. credit your account, cancel an account, change your tariff',
    ...brandUrls('energy'),
  },
  communications: {
    segment:              'communications',
    displayName:          sectorToCompanyName('communications'),
    logo:                 assetUrl(logoComms),
    logoAlt:              'Communications Ombudsman Logo',
    sectorNoun:           'communications',
    regulatorName:        'Ofcom',
    regulatorDescription: 'the communications regulator for the United Kingdom',
    // Same sentence with the energy-only "change your tariff" clause dropped. Not replaced with
    // a comms equivalent, because none is sourced in this repo.
    practicalActionText:  'Take practical action, e.g. credit your account, cancel an account',
    ...brandUrls('communications'),
    // phone deliberately unset: the communications support number is not sourced in this repo.
    // Callers degrade to contactUrl, which is confirmed, rather than quoting the energy number.
    // statutoryDisclosures deliberately unset: the registered entity name, company number and
    // accessibility statement URL are not sourced anywhere in this repo, and inventing legal
    // registration details is worse than omitting them. Footer renders the block only when
    // present, so supplying real values is the only change needed.
  },
};

export const DEFAULT_REFERRAL_BRAND: ReferralBrand = 'energy';

/**
 * Resolves the brand from the leading path segment. Returns null for any other path — including
 * the post-lookup /referral/:complaintId pages, which carry no brand and fall back to the
 * persisted value. Only the two known segments ever resolve, so no URL-supplied content can
 * reach branding, links or copy.
 */
export function brandFromPathname(pathname: string): ReferralBrand | null {
  const segment = pathname.split('/')[1];
  return segment === 'energy' || segment === 'communications' ? segment : null;
}

/**
 * Resolves the brand from a complaint's own sector symbol, for the entry points that have the
 * case in hand rather than a URL: the authenticated app's "Complete Referral" action, and the
 * consent-page controller override (CD-8784 Q12).
 *
 * The sector vocabulary (lib/complaints/sectorsList) is already 'energy' / 'communications', so
 * this is a narrowing rather than a translation — but it must stay a narrowing, because the
 * symbol arrives from the API as a plain string and an unrecognised value must not become a
 * URL segment. The comparison tolerates case and surrounding whitespace (CD-8814): the backend
 * deliberately matches sector symbols case-insensitively (SectorTypes.forCode,
 * OmbudsmanBrandResolver), and the details endpoint returns the raw database symbol verbatim, so
 * a symbol that differs only in casing must not silently flip a journey to the fallback brand.
 *
 * The fallback is for null/unrecognised symbols. It defaults to energy rather than throwing: the
 * CMS column is nullable, so a case with no sector recorded is a real state and not an error, and
 * every case that predates comms onboarding is an energy one — the same reason CD-8652 redirects
 * /referral there. Callers that already know a brand from the journey URL should pass it instead
 * (CD-8814): on a communications journey, energy is not a safe fallback for a consent surface
 * that names the data controller.
 */
export function brandFromSector(
  symbol: string | null | undefined,
  fallback: ReferralBrand = DEFAULT_REFERRAL_BRAND,
): ReferralBrand {
  const normalised = symbol?.trim().toLowerCase();
  return normalised === 'energy' || normalised === 'communications' ? normalised : fallback;
}

export const referralBrandConfig = (brand: ReferralBrand): ReferralBrandConfig => REFERRAL_BRANDS[brand];

export type BrandContactRoute =
  | { kind: 'phone'; phone: string }
  | { kind: 'contactPage'; url: string; displayName: string };

/**
 * The single place that decides how a dead-ended customer reaches their Ombudsman: the brand's
 * support line when one is sourced, its contact page otherwise. Centralised so no call site can
 * fall back to quoting the wrong Ombudsman's number (the communications config deliberately has
 * no phone). Callers render the route however their context needs; only the decision is shared.
 */
export function brandContactRoute(config: ReferralBrandConfig): BrandContactRoute {
  return config.phone
    ? { kind: 'phone', phone: config.phone }
    : { kind: 'contactPage', url: config.contactUrl, displayName: config.displayName };
}
